import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { LANGS } from "../lib/i18n";
import { useI18n } from "../context/I18nContext";

const flagSrc = (f) => `https://flagcdn.com/w40/${f}.png`;

export const LanguageSwitcher = ({ compact = false }) => {
  const { lang, setLang } = useI18n();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = LANGS.find((l) => l.code === lang) || LANGS[0];

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <div className="relative" ref={ref} data-testid="language-switcher">
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="lang-toggle"
        className="flex items-center gap-2 text-white/80 hover:text-gold transition-colors"
      >
        <img src={flagSrc(current.flag)} alt={current.label} className="w-6 h-4 object-cover rounded-[2px]" />
        {!compact && <span className="font-sans text-[11px] tracking-wide-lux uppercase">{current.code}</span>}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.2 }}
            className="absolute right-0 mt-3 min-w-[170px] glass rounded-xl overflow-hidden py-2 z-50"
          >
            {LANGS.map((l) => (
              <button
                key={l.code}
                data-testid={`lang-option-${l.code}`}
                onClick={() => {
                  setLang(l.code);
                  setOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                  l.code === lang ? "text-gold" : "text-white/70 hover:text-white"
                }`}
              >
                <img src={flagSrc(l.flag)} alt={l.label} className="w-6 h-4 object-cover rounded-[2px]" />
                <span className="font-sans text-sm">{l.label}</span>
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
